import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { provideIcons } from '@ng-icons/core';
import { iconoirArrowRight, iconoirBuilding } from '@ng-icons/iconoir';
import { SupabaseService } from '../../core/supabase.service';
import { AuthService } from '../../core/auth/auth.service';
import { InputTextComponent } from '../../components/forms/input-text/input-text.component';
import { BtnComponent } from '../../components/ui/buttons/btn/btn.component';

/** Longueur maximale du nom, comme à l'inscription (MOKA-360). */
const ORGANISATION_LABEL_MAX = 80;

@Component({
  selector: 'app-setup-organisation',
  imports: [FormsModule, InputTextComponent, BtnComponent],
  providers: [provideIcons({ iconoirBuilding, iconoirArrowRight })],
  templateUrl: './setup-organisation.component.html',
  styleUrl: './setup-organisation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SetupOrganisationComponent {

  private readonly supabase = inject(SupabaseService);
  private readonly auth = inject(AuthService);

  public readonly maxLength = ORGANISATION_LABEL_MAX;
  public readonly organisationName = signal('');
  /** Erreur liée au champ (nom vide, déjà pris), affichée sous celui-ci. */
  public readonly fieldError = signal<string | null>(null);
  /** Erreur générale (réseau, bascule). */
  public readonly errorMessage = signal<string | null>(null);
  public readonly submitting = signal(false);

  public onNameChange(value: string): void {
    this.organisationName.set(value ?? '');
    this.fieldError.set(null);
  }

  /**
   * Crée l'organisation puis bascule dessus.
   *
   * Tout est fait par l'edge function `create-new-organisation` : organisation,
   * rôle SUPER_ADMIN du créateur, appartenance, calendrier, agence, grille,
   * position et abonnement d'essai. Le client ne crée plus ces lignes lui-même,
   * et n'écrit plus `profiles.organisation_id` — la bascule passe par
   * `AuthService.switchOrganisation()`, qui recharge l'application.
   */
  async createOrganisation(event?: Event): Promise<void> {
    event?.preventDefault();
    const label = this.organisationName().replace(/\s+/g, ' ').trim();
    if (this.submitting()) return;
    if (!label) {
      this.fieldError.set('Indiquez le nom de votre organisation, par exemple celui de votre entreprise.');
      return;
    }

    this.submitting.set(true);
    this.errorMessage.set(null);
    this.fieldError.set(null);

    const { data, error } = await this.supabase.client.functions.invoke(
      'create-new-organisation',
      { body: { label } }
    );

    if (error || !data?.organisation_id) {
      this.submitting.set(false);
      const status = (error as { context?: Response } | null)?.context?.status;
      if (status === 409) {
        this.fieldError.set('Ce nom est déjà utilisé par une autre organisation. Ajoutez par exemple votre ville ou votre activité.');
      } else {
        this.errorMessage.set('L\'organisation n\'a pas pu être créée. Vérifiez votre connexion internet, puis réessayez.');
      }
      return;
    }

    const switched = await this.auth.switchOrganisation(data.organisation_id);
    this.submitting.set(false);

    if (!switched) {
      this.errorMessage.set('L\'organisation a bien été créée, mais la bascule a échoué. Rechargez la page pour y accéder.');
    }
  }

}
